import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MtmContextService } from '@mtm/context-ui-angular';
import { DRIVE_CONTEXT_MINTER } from '@mtm/drive/components';
import type { RecentActivityEntry } from '@mtm/drive/core';
import type { Brand } from '../interfaces/brand/brand.interface';
import { AuthService } from '../services/auth.service';
import { BrandService } from '../services/brand.service';
import { FeatureConfigService } from '../services/feature-config.service';
import { PlatformCommentsChatStateService } from '../services/platform-comments-chat-state.service';
import { DriveOrgAnchorResolver, type OrgAnchorResolution } from './drive-org-anchor-resolver.service';
import type { OrgEntityTarget } from './org-entity-routes';
import { ProjectBrandResolver } from './project-brand-resolver.service';
import { navigateToRecentActivity, recentActivityContextState } from './drive-recent-ai.utils';

/**
 * Action the drive surface registers so the top-nav recent-AI dropdown can open
 * a clicked conversation inside the embedded explorer.
 */
export interface DriveRecentAiController {
  openPrompt(entry: RecentActivityEntry): void;
}

export interface PendingRecentActivityNavigation {
  readonly entry: RecentActivityEntry;
  readonly contextUuid: string | null;
}

/**
 * Bridge between the top-nav recent-AI button and the drive surface's explorer.
 * Mirrors `DriveShareUiService`'s controller registration, plus a parking slot
 * for cross-project clicks: the ORIGIN surface stashes the prompt and navigates
 * to the target project; the TARGET surface consumes it once its explorer is
 * mounted (see the drive surface's `consumePendingRecentAi` effect).
 *
 * Entries anchored on an ORG entity (brand / workspace / campaign) take a third path:
 * they have no explorer at all, so they go to their page with a navigation request the
 * org comment shell consumes — see {@link openOnOrgPage}.
 */
@Injectable({ providedIn: 'root' })
export class DriveRecentAiUiService {
  private readonly router = inject(Router);
  private readonly viewContext = inject(MtmContextService);
  private readonly contextMinter = inject(DRIVE_CONTEXT_MINTER);
  private readonly orgAnchors: DriveOrgAnchorResolver = inject(DriveOrgAnchorResolver);
  private readonly platformChatState: PlatformCommentsChatStateService = inject(PlatformCommentsChatStateService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly brandService: BrandService = inject(BrandService);
  private readonly featureConfig: FeatureConfigService = inject(FeatureConfigService);
  private readonly projectBrands: ProjectBrandResolver = inject(ProjectBrandResolver);

  private _controller: DriveRecentAiController | null = null;
  private _pending: PendingRecentActivityNavigation | null = null;
  /** Click ordering: only the latest click may still route after an awaited step. */
  private _clickSeq = 0;

  public registerController(controller: DriveRecentAiController): void {
    this._controller = controller;
  }

  /** Identity-checked so a late destroy never clobbers a newer surface's registration. */
  public unregisterController(controller: DriveRecentAiController): void {
    if (this._controller === controller) {
      this._controller = null;
    }
  }

  /**
   * From the rail's Recent activity: route the click to whatever owns the entry.
   *
   * An org-entity anchor is settled FIRST, before any registered surface is offered the
   * entry: such a row is project-less, and a drive surface would otherwise treat it as
   * one of its own (My Own Space adopts every project-less entry, a project surface
   * forwards it as personal) and open a draft on an element its explorer cannot address.
   */
  public openPrompt(entry: RecentActivityEntry): void {
    void this.routePrompt(entry, ++this._clickSeq);
  }

  /**
   * `seq` makes the LAST click win. Settling an org anchor can take a round trip, and
   * without this an uncached slow row that was clicked first would still route when it
   * came back — navigating away from, or hijacking the feed of, whatever the user asked
   * for since. A superseded click drops silently: it must not even leave a navigation
   * request behind, or the next org page to mount would consume it.
   */
  private async routePrompt(entry: RecentActivityEntry, seq: number): Promise<void> {
    const org: OrgAnchorResolution | null = await this.resolveOrgAnchor(entry);
    if (seq !== this._clickSeq) return;
    if (org !== null) {
      await this.openOnOrgPage(entry, org, seq);
      return;
    }

    // A project route is reachable from any brand, so this is consistency rather than
    // access: without it the chrome keeps naming the brand the user came from.
    const projectBrand: Brand | null = await this.projectBrandToSwitchTo(entry);
    if (seq !== this._clickSeq) return;
    if (
      projectBrand !== null &&
      entry.projectUuid !== null &&
      (await this.openAcrossBrands(
        entry,
        { commands: ['/projects', entry.projectUuid], queryParams: {} },
        projectBrand,
        seq
      ))
    ) {
      return;
    }

    if (this._controller !== null) {
      this._controller.openPrompt(entry);
      return;
    }
    this.openOnItsSurface(entry);
  }

  /**
   * Only a project-less DIRECTORY anchor can be an org page, so nothing else pays for
   * the lookup. A personal-space folder resolves to null (one cached read) and routes on.
   */
  private resolveOrgAnchor(entry: RecentActivityEntry): Promise<OrgAnchorResolution | null> {
    if (entry.projectUuid !== null || entry.anchorKind !== 'DIRECTORY' || !entry.elementUuid) {
      return Promise.resolve(null);
    }
    return this.orgAnchors.resolve(entry.elementUuid);
  }

  /**
   * Opens an org entity's feed on the entry — the brand / workspace / campaign page's
   * comment shell, which has no explorer and reads its target from the chat state's
   * navigation request (`DriveOrgFeedContextSync` consumes it on mount, or replays it
   * straight away when that feed is already restored).
   *
   * Deliberately NOT the project path's parking slot: no explorer mounts on these pages
   * to consume it, and a parked project-less entry would be taken by the next My Own
   * Space mount instead — the misroute this whole path exists to prevent.
   */
  private async openOnOrgPage(entry: RecentActivityEntry, resolution: OrgAnchorResolution, seq: number): Promise<void> {
    if (this.isShowingOrgFeed(resolution, entry.elementUuid)) {
      this.requestOrgFeedTarget(entry);
      return;
    }

    const brand: Brand | null = this.brandToSwitchTo(resolution.ids.brand);
    if (brand !== null && (await this.openAcrossBrands(entry, resolution.target, brand, seq))) {
      return;
    }

    this.requestOrgFeedTarget(entry);
    void this.router.navigate(resolution.target.commands, { queryParams: resolution.target.queryParams });
  }

  /** Addresses the clicked row on the org feed that is about to mount (or is mounted). */
  private requestOrgFeedTarget(entry: RecentActivityEntry): void {
    const isDraft: boolean = entry.aiState === 'DRAFT';
    this.platformChatState.requestNavigation({
      channelId: entry.channelUuid,
      messageId: entry.messageUuid,
      ...(entry.parentMessageUuid && !isDraft ? { parentMessageId: entry.parentMessageUuid } : {}),
      // A draft is not in the feed, so it is resumed into the composer rather than focused.
      ...(isDraft ? { draftMessageId: entry.messageUuid } : {}),
      targetId: entry.elementUuid,
    });
  }

  /**
   * The brand this entry needs active, or null when the current one already serves it.
   *
   * Matched against the user's own brand list, as `BrandService.resolveActiveBrand` does, so
   * a brand they have lost access to falls through instead of switching to something
   * unusable. An unloaded list (`brands()` empty before `loadUserBrands` settles) also
   * falls through — the navigation then behaves as it did before this path existed.
   */
  private brandToSwitchTo(brandId: string | null | undefined): Brand | null {
    if (!brandId || brandId === this.authService.getCurrentBrandId()) return null;
    return this.brandService.brands().find((candidate: Brand) => candidate.id === brandId) ?? null;
  }

  /**
   * The brand a PROJECT entry needs active, or null when no switch applies.
   *
   * Gated on `canSwitchBrand()` first — a single-brand user can never be in this
   * situation, so they never pay the lookup behind it. Unlike an org page a project
   * route is addressable from any brand (`/projects/:id` carries no brand and its route
   * has no feature guard), so this is cosmetic consistency: the chrome, the brand
   * switcher and the breadcrumb label follow what is on screen.
   */
  private async projectBrandToSwitchTo(entry: RecentActivityEntry): Promise<Brand | null> {
    if (!entry.projectUuid || !this.brandService.canSwitchBrand()) return null;
    return this.brandToSwitchTo(await this.projectBrands.resolve(entry.projectUuid));
  }

  /**
   * Opens an entry under another brand at `target`: switch the brand, then land there with
   * a FULL DOCUMENT LOAD — the same nuke-everything semantics as `BrandService.switchBrand`,
   * which is the only way the app changes brand. Returns false when it cannot carry the
   * entry across the reload, leaving the caller to route it as usual.
   *
   * A reload is why the target rides in `?ctx=` instead of an in-memory handoff: neither the
   * org feed's navigation request nor the project surface's parked entry survives one.
   * Context does, and both surfaces restore the same keys from it — `readOrgFeedContext`
   * reads `dsd` + `dfd`, and the explorer's own sync reads `dfd` too, resolving a file
   * anchor's directory itself when the cache misses. Nothing is written to the request slot
   * or the parking slot: a reload would strand either for the next page to consume.
   *
   * Soft-switching in-SPA was the alternative and is not safe for an org target:
   * `clearFeatureConfig()` empties the `BehaviorSubject` that `CanAccessWorkspace` reads,
   * which makes `/workspaces` bounce to `/workspaces/projects`, and the report /
   * self-trainer menus are cached until a `user.switched` or a reload, so they would still
   * describe the previous brand.
   */
  private async openAcrossBrands(
    entry: RecentActivityEntry,
    target: OrgEntityTarget,
    brand: Brand,
    seq: number
  ): Promise<boolean> {
    const state: Record<string, string | boolean> | null = recentActivityContextState(this.viewContext.state(), entry);
    if (state === null) {
      // Nothing addressable to carry across the reload (a SURFACE_VIEW anchor).
      return false;
    }

    const ctxId: string = await this.contextMinter.create(state);
    // Last checkpoint: past this the document is gone and a stale click cannot be undone.
    if (seq !== this._clickSeq) return true;

    const url: string = this.router.serializeUrl(
      this.router.createUrlTree(target.commands, {
        queryParams: { ...target.queryParams, ctx: ctxId },
      })
    );
    // Order as in `switchBrand`: persist the brand (patches `auth_user.brandId`, clears
    // brand-scoped storage, emits `BrandUpdatedEvent`), drop the flags cache, re-warm it,
    // then leave. Past `setMyBrand` the switch is committed, so a click that arrives during
    // the re-warm no longer diverts this navigation.
    this.authService.setMyBrand(brand);
    this.featureConfig.clearFeatureConfig();
    // Re-warm BEFORE leaving. `storeConfig` persists the new brand's flags under its scope
    // key, so after the reload `loadFeatureConfig` hits a matching cache and emits
    // synchronously. `CanAccessWorkspace` guards `/workspaces` off the CURRENT value of that
    // subject and reads null as "feature off" — reloading with an empty cache bounces the
    // brand page to `/workspaces/projects`. This await is why `switchBrand` resolves
    // `getInitialRoute()` (which loads the config) before its own document load.
    // The `BrandUpdatedEvent` above already started this request; `loadFeatureConfig`
    // dedupes on scope, so this joins it rather than issuing a second one.
    try {
      await firstValueFrom(this.featureConfig.loadFeatureConfig());
    } catch {
      // Leave anyway: the reload re-requests it, which is no worse than not switching.
    }
    this.navigateToUrl(url);
    return true;
  }

  /** Full document load. Overridable seam for tests, as `BrandService.navigateToUrl` is. */
  protected navigateToUrl(url: string): void {
    window.location.href = url;
  }

  /**
   * Whether the feed that just took the request is the one on screen — then navigating
   * is not only pointless but harmful, since `onSameUrlNavigation: 'reload'` would tear
   * the page down under the replay.
   *
   * The ANCHOR ALONE DOES NOT ANSWER THIS. A chat context outlives the page that set it
   * (`driveAnchorUuid` is only cleared by a later `setContext` naming a different page,
   * and My Own Space never sets one at all), so after leaving a campaign for My Own
   * Space the campaign is still the standing anchor — and skipping on that match left
   * the user stranded there with no feed to consume the request. The page's own URL
   * matcher is what makes it a live feed rather than a remembered one.
   */
  private isShowingOrgFeed(resolution: OrgAnchorResolution, elementUuid: string): boolean {
    return (
      resolution.page.matchesUrl(this.router.url) &&
      this.platformChatState.getCurrentValue().driveAnchorUuid === elementUuid
    );
  }

  /**
   * Navigates to the entry's project (or My Own Space when it has none) and parks it
   * there; that surface opens it once its explorer mounts.
   *
   * Org-entity anchors never reach this — {@link openPrompt} settles them first — and
   * would be misrouted to My Own Space if they did, since they are project-less too.
   */
  public openOnItsSurface(entry: RecentActivityEntry): void {
    let pending: PendingRecentActivityNavigation | null = null;
    void navigateToRecentActivity(
      this.router,
      this.contextMinter,
      this.viewContext.state(),
      entry,
      entry.projectUuid ? ['/projects', entry.projectUuid] : ['/my-space'],
      undefined,
      {
        prepare: (contextUuid: string) => {
          pending = this.stashPending(entry, contextUuid);
        },
        cancel: () => {
          if (pending !== null) this.clearPending(pending);
        },
      }
    );
  }

  /** Park an activity entry for the surface a navigation is about to mount. */
  public stashPending(entry: RecentActivityEntry, contextUuid: string): PendingRecentActivityNavigation {
    const pending: PendingRecentActivityNavigation = { entry, contextUuid };
    this._pending = pending;
    return pending;
  }

  /** Clear only the handoff that initiated a failed/cancelled navigation. */
  public clearPending(pending: PendingRecentActivityNavigation): void {
    if (this._pending === pending) this._pending = null;
  }

  /** One-shot read: the consuming surface takes ownership of the parked entry. */
  public takePending(): PendingRecentActivityNavigation | null {
    const pending: PendingRecentActivityNavigation | null = this._pending;
    this._pending = null;
    return pending;
  }

  /** Consume only an entry owned by the surface that has actually mounted. */
  public takePendingForProject(projectUuid: string | null): PendingRecentActivityNavigation | null {
    if (this._pending?.entry.projectUuid !== projectUuid) return null;
    return this.takePending();
  }
}
